/**
 * Validating an autotile definitions file (content/game/maps/autotiles.json): structure always, tile
 * references when the caller passes the tilesets' tile counts.
 *
 * Its own leaf, apart from autotile_library.ts, because of WHO calls it: the content compiler
 * (game-common/content/validate.ts) checks a candidate's definitions before any runtime catalog
 * exists, while autotile_library.ts loads the published definitions from that catalog at import. So
 * this module imports only the wall kind's validator and types -- no catalog, no models, no IO.
 */
import type { AutotileDefinitionFile, AutotileFileEntry, AutotileKind, TileRef } from "./autotile_library"
import { validateWallDefinition } from "./autotile_wall"

export const AUTOTILE_KINDS: readonly AutotileKind[] = ["bitmask", "uniform", "platform", "wall"]

export const PLATFORM_VARIANT_COUNT = 23

const ID_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*(?::\d+)?$/
const NAME_PATTERN = /^[A-Za-z0-9_-]+$/
const LAYER_PATTERN = /^[a-z][a-z0-9]*$/

export interface AutotileValidationOptions {
    /** Known tilesets and their tile counts. When given, every tile reference is checked against it. */
    tilesets?: Record<string, number>
}

const isInt = (value: unknown): value is number => typeof value === "number" && Number.isInteger(value)
const isIndex = (value: number) => isInt(value) && value >= 1 && value <= 47

/**
 * Every problem with a definitions file, as readable sentences. Empty means valid.
 *
 * Structure is always checked. Tile references (does the tileset exist, is the position inside
 * it) are checked when `options.tilesets` is given: this module cannot read tilesets itself, so
 * the test passes the real ones in and the loader checks only what it can see.
 */
export const validateAutotileFile = (file: unknown, options: AutotileValidationOptions = {}): string[] => {
    const errors: string[] = []
    const root = file as AutotileDefinitionFile
    if (!root || typeof root !== "object") return ["autotile definitions: expected an object"]
    if (root.version !== 1) errors.push(`autotile definitions: unsupported version ${JSON.stringify(root.version)}`)
    if (!Array.isArray(root.definitions)) return [...errors, "autotile definitions: `definitions` must be an array"]

    const ids = new Set<string>()
    const templates = new Set<string>()
    root.definitions.forEach((definition: AutotileFileEntry, position) => {
        const where = `definition ${typeof definition?.id === "string" ? `"${definition.id}"` : `#${position}`}`
        const fail = (message: string) => errors.push(`${where}: ${message}`)
        if (!definition || typeof definition !== "object") {
            fail("expected an object")
            return
        }
        if (typeof definition.id !== "string" || !ID_PATTERN.test(definition.id)) {
            fail("id must be lower-case words joined by '-', optionally ':<number>' (e.g. grass, lpc-terrain:353)")
        } else if (ids.has(definition.id)) {
            fail("duplicate id")
        } else {
            ids.add(definition.id)
        }
        if (typeof definition.name !== "string" || !definition.name.trim()) fail("name must be a non-empty string")
        if (!AUTOTILE_KINDS.includes(definition.kind)) {
            fail(`unsupported kind ${JSON.stringify(definition.kind)} (expected one of ${AUTOTILE_KINDS.join(", ")})`)
            return
        }

        const tilesetId = (definition as any).tilesetId
        const tileCount = options.tilesets?.[tilesetId]
        if (typeof tilesetId !== "string" || !NAME_PATTERN.test(tilesetId)) {
            fail("tilesetId must name a tileset")
        } else if (options.tilesets && tileCount === undefined) {
            fail(`unknown tileset "${tilesetId}"`)
        }
        const tile = (label: string, value: unknown, { allowNone = false } = {}) => {
            if (!isInt(value) || value < (allowNone ? -1 : 0)) {
                fail(`${label} must be a tile position (an integer${allowNone ? " >= -1" : " >= 0"}), got ${JSON.stringify(value)}`)
            } else if (tileCount !== undefined && value >= tileCount) {
                fail(`${label} ${value} is outside ${tilesetId}, which has ${tileCount} tiles`)
            }
        }
        const layer = (label: string, value: unknown, required: boolean) => {
            if (value === undefined && !required) return
            if (typeof value !== "string" || !LAYER_PATTERN.test(value)) fail(`${label} must be a layer id such as map1`)
        }
        // A tile in a tileset the reference names itself: { tilesetId, tilePos }.
        const tileRef = (label: string, value: unknown) => {
            const ref = value as TileRef
            if (!ref || typeof ref !== "object" || Array.isArray(ref)) {
                fail(`${label} must be a tile reference { "tilesetId", "tilePos" }`)
                return
            }
            const count = options.tilesets?.[ref.tilesetId]
            if (typeof ref.tilesetId !== "string" || !NAME_PATTERN.test(ref.tilesetId)) fail(`${label}.tilesetId must name a tileset`)
            else if (options.tilesets && count === undefined) fail(`${label}: unknown tileset "${ref.tilesetId}"`)
            if (!isInt(ref.tilePos) || ref.tilePos < 0) fail(`${label}.tilePos must be a tile position (an integer >= 0), got ${JSON.stringify(ref.tilePos)}`)
            else if (count !== undefined && ref.tilePos >= count) fail(`${label}.tilePos ${ref.tilePos} is outside ${ref.tilesetId}, which has ${count} tiles`)
            Object.keys(ref).forEach(key => key !== "tilesetId" && key !== "tilePos" && fail(`${label}: unknown field "${key}"`))
        }

        switch (definition.kind) {
            case "bitmask": {
                tile("tilePos", definition.tilePos)
                layer("layerId", definition.layerId, false)
                if (!definition.tiles || typeof definition.tiles !== "object" || Array.isArray(definition.tiles)) {
                    fail("tiles must map autotile indices to tile positions")
                } else {
                    Object.entries(definition.tiles).forEach(([index, value]) => {
                        if (!/^\d+$/.test(index) || !isIndex(Number(index))) fail(`tiles: "${index}" is not an autotile index (1-47)`)
                        tile(`tiles[${index}]`, value)
                    })
                }
                if (definition.fallback !== undefined) tile("fallback", definition.fallback, { allowNone: true })
                if (definition.mapLayerYDelta !== undefined && !isInt(definition.mapLayerYDelta)) fail("mapLayerYDelta must be an integer")
                if (definition.tiledTemplate !== undefined && typeof definition.tiledTemplate !== "boolean") fail("tiledTemplate must be true or false")
                if (definition.tiledTemplate) {
                    const key = `${tilesetId}:${definition.tilePos}`
                    if (templates.has(key)) fail(`a second Tiled-template set for ${key}`)
                    templates.add(key)
                }
                if (definition.notes !== undefined) {
                    Object.entries(definition.notes || {}).forEach(([index, note]) => {
                        if (!isIndex(Number(index)) || typeof note !== "string") fail(`notes: "${index}" must be an index with a string note`)
                    })
                }
                break
            }
            case "uniform": {
                layer("layerId", definition.layerId, true)
                tile("previewTilePos", definition.previewTilePos)
                if (!Array.isArray(definition.indices) || !definition.indices.every(isIndex)) {
                    fail("indices must be a list of autotile indices (1-47)")
                } else if (new Set(definition.indices).size !== definition.indices.length) {
                    fail("indices repeat")
                }
                break
            }
            case "platform": {
                tile("tilePos", definition.tilePos)
                if (!Array.isArray(definition.variants) || definition.variants.length !== PLATFORM_VARIANT_COUNT) {
                    fail(`variants must list exactly ${PLATFORM_VARIANT_COUNT} tile positions`)
                } else {
                    definition.variants.forEach((value, i) => tile(`variants[${i}]`, value, { allowNone: true }))
                }
                if (definition.preview !== undefined) {
                    const preview = definition.preview
                    layer("preview.layerId", preview?.layerId, true)
                    if (typeof preview?.autoBlocking !== "boolean" || typeof preview?.autoOcclusion !== "boolean") {
                        fail("preview needs autoBlocking and autoOcclusion flags")
                    }
                }
                break
            }
            case "wall":
                validateWallDefinition(definition, { fail, tile, tileRef, layer })
                break
        }
    })
    return errors
}

/** The definitions in a file, or a thrown error listing everything wrong with it. */
export const parseAutotileFile = (file: unknown, options?: AutotileValidationOptions): AutotileFileEntry[] => {
    const errors = validateAutotileFile(file, options)
    if (errors.length) throw new Error(`Invalid autotile definitions:\n  ${errors.join("\n  ")}`)
    return (file as AutotileDefinitionFile).definitions
}
